import './HospitalEstadistica.css';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, DatePicker, Card, Modal,
  Form, Row, Space, Table, Typography, Statistic }        from 'antd';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {CheckOutlined, PhoneOutlined, CalendarOutlined, 
  UserOutlined, DownloadOutlined} from '@ant-design/icons';
import dayjs                            from "dayjs";
import moment                           from 'moment';
import { ServicesHospital }             from '../../services/ServicesHospital';
import _                                from 'lodash';
import { getFormatedItem }               from '../../utils/Utils';

// Constants
const { Title } = Typography;
const stateInitial = {
  currentCalls: [],
  rawTypeCalls: [],
  categorias: [],
  desglose: null,
  answeredCalls: [],
  repetitionsCalls: [],
  kpi:{},
  inicio: "",
  final: ""
}
  
// Prefijo de categoría: DISP-01 -> DISP. Sin guion se queda igual; null -> SIN_DATOS
const getPrefijo = (codigo) =>
  codigo === null || codigo === undefined ? "SIN_DATOS" : String(codigo).split("-")[0];

// Etiqueta de leyenda: "Departamento (PREFIJO)"
const getLeyenda = (label, prefijo) =>
  label === prefijo ? prefijo : `${label} (${prefijo})`;

// Etiqueta dentro del quesito: porcentaje y (prefijo) en dos líneas
const RADIAN = Math.PI / 180;
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, payload }) => {
  // Los quesitos muy pequeños no caben; su dato sigue en el tooltip
  if (percent < 0.05) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.6;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central"
      style={{ fontSize: 11, fontWeight: "bold" }}>
      <tspan x={x} dy="-0.5em">{payload.porcentaje}%</tspan>
      <tspan x={x} dy="1.2em">({payload.key})</tspan>
    </text>
  );
};

// { "DISP-01": 12, ... } -> [{ codigo, total, porcentaje }] ordenado, % sobre el grupo
const toDetalle = (codigos) => {
  const totalGrupo = _.sum(Object.values(codigos));
  return _.orderBy(
    Object.entries(codigos).map(([codigo, total]) => ({
      codigo,
      total,
      porcentaje: totalGrupo > 0 ? +((100 * total) / totalGrupo).toFixed(2) : 0
    })),
    ["total"], ["desc"]
  );
}

// Tooltip resumido: el desglose por código se abre al pinchar
const ResumenCategoria = ({ titulo, color, total }) => (
  <div className='ycs-container-tooltip' style={{ height: 'auto' }}>
    <p style={{ fontWeight: "bold", color }}>{titulo}</p>
    <p>Total: {total}</p>
    <p style={{ fontStyle: "italic" }}>Pincha para ver el desglose</p>
  </div>
);

export const HospitalGrafica = () => {
  ////////
  // Hooks
  ////////
  const [state, setState] = useState(stateInitial);
  
  useEffect(() => {
    // Update title
    document.title = "COFLPA - estadística";
    
    getData({ start: moment().set("date",1), fin: moment() });
    getCategorias();
  }, []);

  const getCategorias = async() => {
    try {
      const resp = await ServicesHospital.getCategorias();
      setState(prevState => (
        {
          ...prevState,
          categorias: Array.isArray(resp) ? resp : []
        }
      ));
    } catch (e) {
      // Sin categorías se muestra el prefijo en vez del departamento
    }
  }

  // codigo -> "Cuándo se usa" (pregunta_tipo) de la categoría
  const cuandoSeUsa = useMemo(() => {
    const map = {};
    state.categorias.forEach(c => { map[c.codigo] = c.pregunta_tipo; });
    return map;
  }, [state.categorias]);

  // Llamadas por tipo agrupadas por prefijo de categoría (DISP, ECON...)
  const { typeCalls, groups, pieTypeCalls } = useMemo(() => {
    const departamentos = {};
    state.categorias.forEach(c => {
      const prefijo = getPrefijo(c.codigo);
      if (!departamentos[prefijo] && c.departamento) {
        departamentos[prefijo] = c.departamento;
      }
    });

    const groupsMap = {};
    const typeCalls = state.rawTypeCalls.map(d => {
      const row = { dia: d.dia, detalle: {} };
      d.opciones.forEach(o => {
        const codigo = o.nombre === null ? "SIN_DATOS" : o.nombre;
        const prefijo = getPrefijo(o.nombre);
        const total = +o.total || 0;
        if (!groupsMap[prefijo]) {
          groupsMap[prefijo] = {
            key: prefijo,
            label: departamentos[prefijo] || prefijo,
            leyenda: getLeyenda(departamentos[prefijo] || prefijo, prefijo),
            color: getFormatedItem(prefijo === "SIN_DATOS" ? "null" : prefijo).color,
            total: 0,
            codigos: {}
          };
        }
        const g = groupsMap[prefijo];
        g.total += total;
        g.codigos[codigo] = (g.codigos[codigo] || 0) + total;
        row[prefijo] = (row[prefijo] || 0) + total;
        row.detalle[prefijo] = row.detalle[prefijo] || {};
        row.detalle[prefijo][codigo] = (row.detalle[prefijo][codigo] || 0) + total;
      });
      return row;
    });

    const groups = _.sortBy(Object.values(groupsMap), "label");
    const totalPie = _.sumBy(groups, "total");
    const pieTypeCalls = groups
      .filter(g => g.total > 0)
      .map(g => ({
        key: g.key,
        nombre: g.leyenda,
        color: g.color,
        total: g.total,
        porcentaje: totalPie > 0 ? +((100 * g.total) / totalPie).toFixed(2) : 0,
        detalle: toDetalle(g.codigos)
      }));

    return { typeCalls, groups, pieTypeCalls };
  }, [state.rawTypeCalls, state.categorias]);

  const getData = async(values) => {
    const start = values.start.format("YYYY-MM-DD");
    const fin = values.fin.format("YYYY-MM-DD");
    setState(prevState => (
      {
        ...prevState,
        inicio: start,
        final: fin
      }
    ));
    getCurrentCalls(start, fin);
    getTypeCalls(start, fin);
    getAnsweredCalls(start, fin);
    getRepetitionsCalls(start, fin);
    getKPI(start, fin);
  }
  const getDatos = async() => {
    await ServicesHospital.getDatos(state.inicio, state.final);
  }

  const getCurrentCalls = async(start, fin) => {
    const resp = await ServicesHospital.getConcurrents(start, fin);

    // Update state
    setState(prevState => (
      {
        ...prevState,
        currentCalls: resp
      }
    ));
  }

  const getTypeCalls = async(start, fin) => {
    const resp = await ServicesHospital.getTypeCalls(start, fin);

    // Update state
    setState(prevState => (
      {
        ...prevState,
        rawTypeCalls: resp
      }
    ));
  }

  const getAnsweredCalls = async(start, fin) => {
    const resp = await ServicesHospital.getAnsweredCalls(start, fin);
    // Update state
    setState(prevState => (
      {
        ...prevState,
        answeredCalls: resp
      }
    ));
  }

  const getRepetitionsCalls = async(start, fin) => {
    const resp = await ServicesHospital.getRepetitionsCalls(start, fin);
    const repetitionsCalls = [];
    // 1º Calculate percentages
    resp.forEach(i => {
      let total = i.total + i.repeticiones;

      repetitionsCalls.push(
        {
          dia: i.dia,
          unicas: +((100 * i.total ) / total).toFixed(2),
          repetidas: +((100 * i.repeticiones ) / total).toFixed(2)
        }
      );
    })

    // Update state
    setState(prevState => (
      {
        ...prevState,
        repetitionsCalls
      }
    ));
  }

  const getKPI = async(start, fin) => {
    const kpi = await ServicesHospital.getKPI(start, fin);
    // Update state
    setState(prevState => (
      {
        ...prevState,
        kpi
      }
    ));
  }

  // Abre el desglose por código de una categoría
  const openDesglose = (titulo, color, total, detalle) => {
    setState(prevState => ({
      ...prevState,
      desglose: { titulo, color, total, detalle }
    }));
  }

  const closeDesglose = () => {
    setState(prevState => ({ ...prevState, desglose: null }));
  }

  const onClickBar = (group, data) => {
    const row = data?.payload;
    if (!row) return;
    openDesglose(
      `${group.leyenda} · ${row.dia}`,
      group.color,
      row[group.key],
      toDetalle(row.detalle[group.key] || {})
    );
  }

  const onClickPie = (data) => {
    const p = data?.payload || data;
    if (!p || !p.detalle) return;
    openDesglose(`${p.nombre} · ${p.porcentaje}% del total`, p.color, p.total, p.detalle);
  }

  // Tooltip por segmento (shared=false)
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      const i = payload[0];
      return (
        <ResumenCategoria
          titulo={`${label} · ${i.name}`}
          color={i.fill}
          total={i.value} />
      );
    }
    return null;
  };

  const CustomTooltipLlamadas = ({ active, payload, label }) => {
    if (active && payload.length > 0 && payload.length) {
      return (
        <div className='ycs-container-tooltip'>
          <p style={{ fontWeight: "bold" }}>{label}</p>
          <p style={{ fontWeight: "bold" }}>Total: {_.sum(_.map(payload,p=> parseInt(p.value)))}</p>
          {payload.map(i => (
            <p style={{ color: i.fill }}>{i.dataKey}: {i.value}</p>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomTooltipPie = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const p = payload[0].payload;
      return (
        <ResumenCategoria
          titulo={`${p.nombre} · ${p.porcentaje}% del total`}
          color={p.color}
          total={p.total} />
      );
    }
    return null;
  };

  return (
    <div>
      {/* Filters */}
      <Row>
        <Col span={24}>
          <Form name="filters"
            layout='horizontal'
            // We use library dayjs only to this special and rare case
            // due to Ant Design
            initialValues={{ start: dayjs().set("date",1), fin: dayjs() }}
            onFinish={(values) => {
              getData(values);
            }}
          >
            <Space style={{ display: 'flex', marginTop: 8 }} >
              <Form.Item label="Inicio" name="start"
                rules={[{required: true, message:"Fecha de inicio requerida", type: 'object' }]}
              >
                <DatePicker format={"DD/MM/YYYY"}/>
              </Form.Item>
              <Form.Item label="Fin" name="fin"
                rules={[{required: true, message:"Fecha de fin requerida", type: 'object' }]}
              >
                <DatePicker format={"DD/MM/YYYY"}/>
              </Form.Item>
              <Form.Item >
                <Button type="primary" htmlType="submit" style={{marginLeft:'25px'}}>
                  Filtrar
                </Button>
              </Form.Item>
              </Space>
          </Form>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={4}>
          <Card bordered={false}>
            <Statistic groupSeparator="." title="Llamadas totales" value={state.kpi.todas} 
              prefix={<PhoneOutlined />}
              valueStyle={{ color: 'blue' }}/>
          </Card>
        </Col>
        <Col span={4}>
          <Card bordered={false}>
            <Statistic groupSeparator="." title="Respondidas" value={state.kpi.atendidas} 
              prefix={<PhoneOutlined />}
              valueStyle={{ color: 'green' }}/>
          </Card>
        </Col>
        <Col span={4}>
          <Card bordered={false}>
            <Statistic groupSeparator="." title="Perdidas" value={state.kpi.perdidas} 
              prefix={<PhoneOutlined />}
              valueStyle={{ color: 'red' }}/>
          </Card>
        </Col>
        {/* <Col span={4}>
          <Card bordered={false}>
            <Statistic groupSeparator="." title="Gestionadas" value={state.kpi.gestionadas} 
              prefix={<CheckOutlined />}    
              valueStyle={{ color: 'green' }}/>
          </Card>
        </Col>
        <Col span={4}>
          <Card bordered={false}>
            <Statistic groupSeparator="." title="Citas" value={state.kpi.citas} 
              prefix={<CalendarOutlined />}/>
          </Card>
        </Col>
        <Col span={4}>
          <Card bordered={false}>
            <Statistic groupSeparator="." title="Privado" value={state.kpi.privados} 
              prefix={<UserOutlined />}/>
          </Card>
        </Col>  */}

      </Row>
      {/* Graphs */}
      <br/>
      <div className='ycs-container-llamadas-concurrentes'>
        <div className='ycs-graph'>
          <div className='ycs-title-llamadas-concurrentes'>
            <Title strong level={4}>
              Llamadas por tipo
            </Title>
          </div>

          {typeCalls.length > 0 ?
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={500}
                height={300}
                data={typeCalls}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="dia" />
                <YAxis />
                {/* <Tooltip /> */}
                <Tooltip shared={false} content={<CustomTooltip /> }/>
                <Legend />
                {groups.map(g => (
                  <Bar dataKey={g.key} name={g.leyenda} fill={g.color} key={g.key} stackId="calls"
                    cursor="pointer" onClick={(data) => onClickBar(g, data)}/>
                ))}
              </BarChart>
            </ResponsiveContainer>
          :<EmptyGraph text="No hay llamadas por tipo"/>
          }
        </div>
      </div>

      <Row gutter={16}>
        <Col md={24} lg={12} span={24}>
          <div className='ycs-container-llamadas-concurrentes'>
            <div className='ycs-graph'>
              <div className='ycs-title-llamadas-concurrentes'>
                <Title strong level={4}>
                  Llamadas atendidas
                </Title>
              </div>

              {state.answeredCalls?.length > 0 ?
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    width={500}
                    height={300}
                    data={state.answeredCalls}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dia" />
                    <YAxis />
                    <Tooltip content={<CustomTooltipLlamadas /> }/>
                    <Bar dataKey="atendidas" stackId="a" fill="#82ca9d" />
                    <Bar dataKey="no_atendidas" stackId="a" fill="#E5442D" />
                  </BarChart>
                </ResponsiveContainer>
                :<EmptyGraph text="De momento no hay llamadas atendidas"/> 
              }
            
            </div>
          </div>
        </Col>
        <Col md={24} lg={12} span={24}>
         {/*          <div className='ycs-container-llamadas-atendidas'>
            <div className='ycs-mini-graph'>
              <div className='ycs-title-llamadas-concurrentes'>
                  <Title strong level={4}>
                    Repeticiones (%)
                  </Title>
              </div>

              {state.repetitionsCalls?.length > 0 ?
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    width={500}
                    height={300}
                    data={state.repetitionsCalls}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dia" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="unicas" stackId="a" fill="#82ca9d" />
                    <Bar dataKey="repetidas" stackId="a" fill="#E5442D" />
                  </BarChart>
                </ResponsiveContainer>
                :<EmptyGraph text="De momento no hay repeticiones"/> 
              }
            </div>
          </div> */}
          <div className='ycs-container-llamadas-concurrentes'>
            <div className='ycs-graph'>
              <div className='ycs-title-llamadas-concurrentes'>
                <Title strong level={4}>
                  Llamadas concurrentes
                </Title>
              </div>
              {state.currentCalls?.length > 0 ?
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    width={500}
                    height={100}
                    data={state.currentCalls}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hora" />
                    <YAxis />
                    <Tooltip/>
                  
                    <Bar dataKey="maximo" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              :<EmptyGraph text="De momento no hay llamadas concurrentes"/> 
              }
          </div>
      </div>
        </Col>
      </Row>

      {/* Llamadas por tipo (%) - círculo de ~5 cm de diámetro (190px) */}
      <div className='ycs-container-llamadas-concurrentes'>
        <div className='ycs-graph' style={{ height: 'auto' }}>
          <div className='ycs-title-llamadas-concurrentes'>
            <Title strong level={4}>
              Llamadas por tipo (%)
            </Title>
          </div>

          {pieTypeCalls?.length > 0 ?
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={pieTypeCalls}
                  dataKey="total"
                  nameKey="nombre"
                  cx="50%"
                  cy="45%"
                  outerRadius={120}
                  label={renderPieLabel}
                  labelLine={false}
                  cursor="pointer"
                  onClick={onClickPie}
                >
                  {pieTypeCalls.map(p => (
                    <Cell key={p.key} fill={p.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltipPie />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          :<EmptyGraph text="No hay llamadas por tipo"/>
          }
        </div>
      </div>

      <Modal
        open={!!state.desglose}
        title={state.desglose ? (
          <span style={{ color: state.desglose.color }}>{state.desglose.titulo}</span>
        ) : ""}
        onCancel={closeDesglose}
        footer={null}
        width={850}
      >
        {state.desglose && (
          <>
            <p>Total de llamadas: <strong>{state.desglose.total}</strong></p>
            <Table
              size="small"
              className="ycs-desglose-table"
              pagination={false}
              rowKey="codigo"
              dataSource={state.desglose.detalle}
              columns={[
                { title: "Código", dataIndex: "codigo" },
                { title: "Cuándo se usa", dataIndex: "codigo",
                  render: codigo => (
                    <span style={{ fontSize: 11, color: "#666", lineHeight: 1.25, display: "block" }}>
                      {cuandoSeUsa[codigo] || "-"}
                    </span>
                  ) },
                { title: "Llamadas", dataIndex: "total", align: "right" },
                { title: "%", dataIndex: "porcentaje", align: "right",
                  render: v => `${v}%` }
              ]}
            />
          </>
        )}
      </Modal>

      <Button type="primary" style={{float: 'right'}}
        icon={<DownloadOutlined />}
        onClick={() => getDatos()}>
        Descargar
      </Button>


    </div>
  )
}

export const EmptyGraph = ({ text }) => {
  return (
    <div className='ycs-empty-graph'>
      {text}
    </div>
  );
}